import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import type { MyWorkOrderDetail, MyWorkOrderUpdate } from "../api/types";
import { formatDateTime, formatWoId, woStatusLabel } from "../lib/workorders";
import { useDictation } from "../lib/dictation";

const MAINT_STATUSES = ["open", "in_progress", "on_hold", "completed"] as const;

export function MaintenanceWorkOrderPage() {
  const { id } = useParams();
  const woId = Number(id);

  const detailQ = useQuery({
    queryKey: ["my-work-order", woId],
    queryFn: () => api.myWorkOrders.get(woId),
    enabled: Number.isInteger(woId),
  });

  if (detailQ.isLoading) {
    return (
      <Layout title="Update Work Order">
        <p className="sub">Loading…</p>
      </Layout>
    );
  }
  if (detailQ.isError || !detailQ.data) {
    return (
      <Layout title="Update Work Order">
        <section className="card">
          <p>That work order is not assigned to you, or could not be found.</p>
          <Link to="/maintenance" className="btn btn-ghost btn-xl">
            Back to My Work Orders
          </Link>
        </section>
      </Layout>
    );
  }

  return <MaintenanceWorkOrderView wo={detailQ.data.workOrder} />;
}

function MaintenanceWorkOrderView({ wo }: { wo: MyWorkOrderDetail }) {
  const queryClient = useQueryClient();

  const [status, setStatus] = useState(wo.status);
  const [completedDate, setCompletedDate] = useState(datePart(wo.completedAt));
  const [completedTime, setCompletedTime] = useState(timePart(wo.completedAt));
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState(false);
  const [pickedItem, setPickedItem] = useState("");
  const [pickedQty, setPickedQty] = useState("");
  const [timerError, setTimerError] = useState<string | null>(null);

  const dictation = useDictation((text) =>
    setNote((prev) => (prev ? `${prev} ${text}` : text)),
  );

  const saveMut = useMutation({
    mutationFn: () => {
      const body: MyWorkOrderUpdate = { status };
      if (status === "completed" && completedDate) {
        body.completedAt = completedTime ? `${completedDate}T${completedTime}` : completedDate;
      }
      if (note.trim()) body.note = note.trim();
      return api.myWorkOrders.update(wo.id, body);
    },
    onSuccess: () => {
      setSaved(true);
      setNote("");
      queryClient.invalidateQueries({ queryKey: ["my-work-order", wo.id] });
      queryClient.invalidateQueries({ queryKey: ["my-work-orders"] });
    },
  });

  const addSupplyMut = useMutation({
    mutationFn: () =>
      api.myWorkOrders.addSupply(wo.id, {
        inventoryItemId: Number(pickedItem),
        quantityUsed: Number(pickedQty),
      }),
    onSuccess: () => {
      setPickedItem("");
      setPickedQty("");
      queryClient.invalidateQueries({ queryKey: ["my-work-order", wo.id] });
    },
  });

  const removeSupplyMut = useMutation({
    mutationFn: (supplyId: number) => api.myWorkOrders.removeSupply(wo.id, supplyId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["my-work-order", wo.id] }),
  });

  const startTimerMut = useMutation({
    mutationFn: () => api.myWorkOrders.startTimer(wo.id),
    onSuccess: () => {
      setTimerError(null);
      queryClient.invalidateQueries({ queryKey: ["my-work-order", wo.id] });
      queryClient.invalidateQueries({ queryKey: ["my-work-orders"] });
    },
    onError: (err) => {
      setTimerError(
        err instanceof Error && err.message === "active_timer"
          ? "Stop your active work order timer before starting another one."
          : "Could not start the timer. Please try again.",
      );
    },
  });

  const stopTimerMut = useMutation({
    mutationFn: () => api.myWorkOrders.stopTimer(wo.id, note.trim() || undefined),
    onSuccess: () => {
      setTimerError(null);
      setNote("");
      queryClient.invalidateQueries({ queryKey: ["my-work-order", wo.id] });
      queryClient.invalidateQueries({ queryKey: ["my-work-orders"] });
    },
    onError: () => setTimerError("Could not stop the timer. Please try again."),
  });

  return (
    <Layout title="Update Work Order">
      <section className="card">
        <h2>
          {formatWoId(wo.id)} — {wo.title}
        </h2>
        <p className="sub">
          {wo.buildingName} · {wo.where}
        </p>
        <p>{wo.description}</p>
      </section>

      <section className="card">
        <h2>Set the status</h2>
        <div className="timer-panel">
          <div>
            <strong>{wo.timerStartedAt ? "Timer running" : "Timer stopped"}</strong>
            <p className="sub">
              {wo.timerStartedAt
                ? `Started ${formatDateTime(wo.timerStartedAt)}.`
                : wo.startTime
                  ? `First started ${formatDateTime(wo.startTime)}.`
                  : "Start the timer when you begin work."}
            </p>
          </div>
          {wo.timerStartedAt ? (
            <button
              className="btn btn-ghost btn-xl"
              onClick={() => stopTimerMut.mutate()}
              disabled={stopTimerMut.isPending}
            >
              {stopTimerMut.isPending ? "Stopping..." : "Stop"}
            </button>
          ) : (
            <button
              className="btn btn-primary btn-xl"
              onClick={() => startTimerMut.mutate()}
              disabled={startTimerMut.isPending || wo.status === "completed"}
            >
              {startTimerMut.isPending ? "Starting..." : "Start"}
            </button>
          )}
        </div>
        {timerError && <p className="field-error">{timerError}</p>}
        <div className="status-grid">
          {MAINT_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={"status-btn" + (status === s ? " selected" : "")}
              onClick={() => {
                setStatus(s);
                setSaved(false);
              }}
            >
              {woStatusLabel(s)}
            </button>
          ))}
        </div>
      </section>

      <section className="card">
        <h2>When was the work completed?</h2>
        <p className="sub">Fill this in when you mark the work order Completed.</p>
        <div className="two-col">
          <label className="field">
            <span>Date completed</span>
            <input
              type="date"
              value={completedDate}
              onChange={(e) => setCompletedDate(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Time completed</span>
            <input
              type="time"
              value={completedTime}
              onChange={(e) => setCompletedTime(e.target.value)}
            />
          </label>
        </div>
        <div className="two-col">
          <label className="field">
            <span>Start time</span>
            <input value={wo.startTime ? formatDateTime(wo.startTime) : "Not started"} readOnly />
          </label>
          <label className="field">
            <span>Assigned date</span>
            <input value={wo.assignedDate ?? "Not assigned"} readOnly />
          </label>
        </div>
      </section>

      <section className="card">
        <h2>Supplies used</h2>
        <p className="sub">
          Record any inventory you used on this job. Each item is saved right away and lowers the
          building's stock count.
        </p>
        {wo.suppliesUsed.length === 0 ? (
          <p className="sub">No supplies recorded yet.</p>
        ) : (
          <ul className="supply-list">
            {wo.suppliesUsed.map((s) => (
              <li key={s.id}>
                <span>
                  <strong>
                    {s.quantityUsed}
                    {s.unitOfMeasure ? ` ${s.unitOfMeasure}` : ""}
                  </strong>{" "}
                  — {s.name}
                </span>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => removeSupplyMut.mutate(s.id)}
                  disabled={removeSupplyMut.isPending}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        {wo.inventoryOptions.length === 0 ? (
          <p className="sub">No inventory items are set up for this building yet.</p>
        ) : (
          <div className="supply-add">
            <label className="field">
              <span>Item</span>
              <select value={pickedItem} onChange={(e) => setPickedItem(e.target.value)}>
                <option value="">Choose an item…</option>
                {wo.inventoryOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name} ({o.quantity}
                    {o.unitOfMeasure ? ` ${o.unitOfMeasure}` : ""} in stock)
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Quantity used</span>
              <input
                type="number"
                min="0"
                step="any"
                value={pickedQty}
                onChange={(e) => setPickedQty(e.target.value)}
              />
            </label>
            <button
              className="btn btn-ghost btn-xl"
              onClick={() => addSupplyMut.mutate()}
              disabled={
                !pickedItem ||
                pickedQty === "" ||
                Number(pickedQty) <= 0 ||
                addSupplyMut.isPending
              }
            >
              {addSupplyMut.isPending ? "Adding…" : "Add supply"}
            </button>
            {addSupplyMut.isError && (
              <p className="field-error">Could not add that supply. Please try again.</p>
            )}
          </div>
        )}
      </section>

      <section className="card">
        <h2>Add a note</h2>
        <div className="note-area">
          <textarea
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={
              dictation.supported
                ? "Type your note here, or tap the microphone to speak."
                : "Type your note here."
            }
          />
          {dictation.supported && (
            <button
              type="button"
              className="mic-btn"
              onClick={dictation.toggle}
              aria-label="Dictate note by voice"
            >
              <span className="mic-ico" aria-hidden="true">
                &#127908;
              </span>
              <span>{dictation.listening ? "Listening… tap to stop" : "Speak"}</span>
            </button>
          )}
        </div>
        {saved && !saveMut.isPending && <p className="saved-note">Update saved.</p>}
        {saveMut.isError && <p className="field-error">Could not save. Please try again.</p>}
        <button
          className="btn btn-primary btn-block btn-xl"
          onClick={() => {
            setSaved(false);
            saveMut.mutate();
          }}
          disabled={saveMut.isPending}
        >
          {saveMut.isPending ? "Saving…" : "Save update"}
        </button>
        <Link to="/maintenance" className="text-link">
          Back to My Work Orders
        </Link>
      </section>

      <section className="card">
        <h2>History</h2>
        {wo.history.length === 0 ? (
          <p className="sub">No updates recorded yet.</p>
        ) : (
          <ul className="timeline">
            {wo.history.map((h, i) => (
              <li key={i}>
                <strong>{formatDateTime(h.at)}</strong> —{" "}
                {h.kind === "status"
                  ? `${h.actor} changed status from ${woStatusLabel(
                      h.from ?? "",
                    )} to ${woStatusLabel(h.to ?? "")}.`
                  : `${h.actor}: ${h.text}`}
              </li>
            ))}
          </ul>
        )}
      </section>
    </Layout>
  );
}

function datePart(iso: string | null): string {
  return iso ? iso.slice(0, 10) : "";
}

function timePart(iso: string | null): string {
  if (!iso || !iso.includes("T")) return "";
  return (iso.split("T")[1] ?? "").slice(0, 5);
}
